// src/components/CabinetEntryCard.jsx
// Tableau de bord : point d'entrée du Cabinet de curiosités. Invisible tant que la fonctionnalité
// n'est pas ouverte pour le compte (drapeau serveur cabinet_curiosites).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { isCabinetEnabled, receivedShares } from "../lib/cabinetService";
import { useCabinetText } from "../lib/cabinetI18n";

export default function CabinetEntryCard() {
  const { user } = useAuth();
  const { t } = useCabinetText();
  const [on, setOn] = useState(false);
  const [collections, setCollections] = useState([]);
  const [cabinets, setCabinets] = useState([]);
  const [received, setReceived] = useState(0);

  useEffect(() => {
    let alive = true;
    if (!user?.id) return undefined;
    (async () => {
      // Une personne invitée à voir un cabinet n'a pas forcément le droit d'en composer : elle voit au moins le lien.
      const [enabled, rec] = await Promise.all([isCabinetEnabled(), receivedShares().catch(() => [])]);
      if (!alive) return;
      setReceived(rec.length);
      if (!enabled) return;
      const [{ data: cols }, { data: cabs }] = await Promise.all([
        supabase.from("collections").select("id,title").eq("client_id", user.id).order("title"),
        supabase.from("curiosity_cabinets").select("id,title,collection_id").eq("status", "active").order("updated_at", { ascending: false }),
      ]);
      if (!alive) return;
      setCollections(cols || []);
      setCabinets(cabs || []);
      setOn(true);
    })().catch(() => {});
    return () => { alive = false; };
  }, [user?.id]);

  if (!on) {
    if (received === 0) return null;
    return (
      <div className="bg-white rounded-lg shadow p-5 mb-6 border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-1" style={{ fontFamily: "Georgia, serif" }}>{t("title")}</h3>
        <p className="text-sm"><Link to="/cabinets/partages" className="underline">{t("cardShared", received)}</Link></p>
      </div>
    );
  }
  const withCabinet = new Set(cabinets.map((c) => c.collection_id));
  const titleOf = (id) => collections.find((c) => c.id === id)?.title;

  return (
    <div className="bg-white rounded-lg shadow p-5 mb-6 border border-gray-100">
      <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{t("cardEyebrow")}</p>
      <h3 className="text-lg font-semibold text-gray-900 mb-1" style={{ fontFamily: "Georgia, serif" }}>{t("title")}</h3>
      <p className="text-sm text-gray-600 mb-3">{t("cardBody")}</p>

      {cabinets.length > 0 && (
        <ul className="mb-3" style={{ listStyle: "none", padding: 0 }}>
          {cabinets.map((c) => (
            <li key={c.id} className="text-sm py-1">
              <Link to={`/collections/${c.collection_id}/cabinet`} className="underline">{c.title}</Link>
              {titleOf(c.collection_id) ? <span className="text-gray-500"> · {titleOf(c.collection_id)}</span> : null}
            </li>
          ))}
        </ul>
      )}

      {collections.filter((c) => !withCabinet.has(c.id)).length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {collections.filter((c) => !withCabinet.has(c.id)).slice(0, 6).map((c) => (
            <Link key={c.id} to={`/collections/${c.id}/cabinet`}
              className="inline-block bg-gray-900 text-white text-sm px-4 py-2 rounded-lg hover:bg-gray-700">
              {t("cardCreate", c.title)}
            </Link>
          ))}
        </div>
      ) : collections.length === 0 ? (
        <p className="text-sm text-gray-500">{t("cardNoCollection")}</p>
      ) : null}

      {received > 0 && (
        <p className="text-sm mt-3"><Link to="/cabinets/partages" className="underline">{t("cardShared", received)}</Link></p>
      )}
    </div>
  );
}
